import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useNotification } from "../../contexts/NotificationContext";
import useEmployeeMasters from "../../hooks/useEmployeeMasters";
import EmployeeMasterKPIs from "./EmployeeMasterKPIs";
import HierarchyTreeView from "./HierarchyTreeView";
import RolePermissionMatrix from "./RolePermissionMatrix";
import ScrollableTableContainer from "../common/ScrollableTableContainer";
import SearchableSelect from "../common/SearchableSelect";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  Download,
  Upload,
  Plus,
  Edit,
  Trash2,
  Eye,
  Users,
  UserCheck,
  UserX,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building,
  IdCard,
  Briefcase,
  MoreVertical,
  X,
  Check,
  AlertCircle,
  FileText,
  Camera,
  UserPlus,
} from "lucide-react";
import EmployeeCreationForm from "./EmployeeCreationForm";
import { mapEmployeeMasterToForm } from "../../utils/employeeMasterPayload";
import EmployeeMasterWorkflow from "./EmployeeMasterWorkflow";
import CredentialGenerationModal from "./CredentialGenerationModal";
import EmployeeDeletionModal from "./EmployeeDeletionModal";
import RetireEmployeeModal from "./RetireEmployeeModal";
import PensionerProfilesPanel from "./PensionerProfilesPanel";
import { formatDate } from "../../utils/dateUtils";
import DepartmentConfiguration from './DepartmentConfiguration';
import GISConfiguration from './GISConfiguration';
import RoleManagement from './RoleManagement';
import ManageDocumentsModal from "./ManageDocumentsModal";
import EmployeeAvatar from "../common/EmployeeAvatar";
import { buildApiUrl } from '../../utils/apiSetup';

// Helper to format bank account number (removes trailing .0 from float values)
const formatAccountNumber = (value) => {
  if (!value) return value;
  const str = String(value);
  // Remove trailing .0 (e.g., "30770409057.0" -> "30770409057")
  return str.replace(/\.0$/, "");
};

const EmployeeManagement = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { masters, fetchHeads } = useEmployeeMasters();
  const [sectionView, setSectionView] = useState("list");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [availableHeads, setAvailableHeads] = useState([]);
  const [advFilters, setAdvFilters] = useState({
    department_id: "",
    role_id: "",
    grade_master_id: "",
    designation_id: "",
    head_code: "",
    reporting_manager_id: "",
  });
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showSuccess, showError } = useNotification();
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [retirementSubFilter, setRetirementSubFilter] = useState("");
  const [sortBy, setSortBy] = useState("emp_code");
  const [sortOrder, setSortOrder] = useState("asc");

  // Selection and bulk operations
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkAction, setBulkAction] = useState("");
  const [showBulkModal, setShowBulkModal] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Modal states
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [credentialEmployee, setCredentialEmployee] = useState(null);
  const [credentialResult, setCredentialResult] = useState(null);
  const [generatingCredentials, setGeneratingCredentials] = useState(false);
  const [canCreateEmployee, setCanCreateEmployee] = useState(false);
  const [canEditEmployee, setCanEditEmployee] = useState(false);
  const [canDeleteEmployee, setCanDeleteEmployee] = useState(false);
  const [canExportExcel, setCanExportExcel] = useState(false);
  const [canExportPDF, setCanExportPDF] = useState(false);
  const [canDownloadTemplate, setCanDownloadTemplate] = useState(false);
  const [canBulkUpload, setCanBulkUpload] = useState(false);
  const [canUploadDocuments, setCanUploadDocuments] = useState(false);
  const [canRetireEmployee, setCanRetireEmployee] = useState(false);
  const [canGenerateCredentials, setCanGenerateCredentials] = useState(false);
  const [canActivateEmployee, setCanActivateEmployee] = useState(false);
  const [canDeactivateEmployee, setCanDeactivateEmployee] = useState(false);
  const [canExportSelected, setCanExportSelected] = useState(false);

  // Deletion modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Retirement modal state
  const [showRetireModal, setShowRetireModal] = useState(false);
  const [employeeToRetire, setEmployeeToRetire] = useState(null);
  const [pensionerRefreshKey, setPensionerRefreshKey] = useState(0);
  const [separationType, setSeparationType] = useState(null);
  const [showSeparationDropdown, setShowSeparationDropdown] = useState(null);

  // Manage Documents modal state
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [documentEmployee, setDocumentEmployee] = useState(null);

  // Debounce search term to avoid too many API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadError, setBulkUploadError] = useState("");
  const [bulkUploadSuccess, setBulkUploadSuccess] = useState("");

  const [uploadPreview, setUploadPreview] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    // Reset to page 1 when search, filters, or sorting change
    setCurrentPage(1);
  }, [
    debouncedSearchTerm,
    filterStatus,
    retirementSubFilter,
    sortBy,
    sortOrder,
    advFilters,
  ]);

  useEffect(() => {
    fetchEmployees();
  }, [
    currentPage,
    filterStatus,
    retirementSubFilter,
    sortBy,
    sortOrder,
    debouncedSearchTerm,
    advFilters,
  ]);

  useEffect(() => {
    // Load heads data for the head-wise filter
    const loadHeads = async () => {
      const heads = await fetchHeads();
      setAvailableHeads(heads);
    };
    loadHeads();
  }, [fetchHeads]);

  useEffect(() => {
    if (filteredEmployees.length > 0) {
      const allCurrentSelected = filteredEmployees.every((emp) =>
        selectedEmployees.includes(emp.id)
      );
      setSelectAll(allCurrentSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedEmployees, filteredEmployees]);

  useEffect(() => {
    if (user && Array.isArray(user.permissions)) {
      const perms = user.permissions;
      const hasCreate =
        perms.includes("employee.create") ||
        perms.includes("employee:create") ||
        perms.includes("*") ||
        perms.includes("hr.*");
      const hasUpdate =
        perms.includes("employee.update") ||
        perms.includes("employee:update") ||
        perms.includes("*") ||
        perms.includes("hr.*");
      const hasDelete =
        perms.includes("employee.delete") ||
        perms.includes("employee:delete") ||
        perms.includes("*") ||
        perms.includes("hr.*");
      const hasExportExcel =
        perms.includes("employee:export") ||
        perms.includes("*") ||
        perms.includes("hr.*");
      const hasExportPDF =
        perms.includes("employee:export:pdf") ||
        perms.includes("*") ||
        perms.includes("hr.*");
      const hasDownloadTemplate =
        perms.includes("employee:import:template") ||
        perms.includes("*") ||
        perms.includes("hr.*");
      const hasBulkUpload =
        perms.includes("employee:import:bulk") ||
        perms.includes("*") ||
        perms.includes("hr.*");
      const hasUploadDoc =
        perms.includes("employee:document:upload") ||
        perms.includes("*") ||
        perms.includes("hr.*");
      const hasRetire =
        perms.includes("employee:retire") ||
        perms.includes("*") ||
        perms.includes("hr.*");
      const hasGenCred =
        perms.includes("employee:credentials:generate") ||
        perms.includes("*") ||
        perms.includes("hr.*");
      const hasActivate =
        perms.includes("employee:activate") ||
        perms.includes("*") ||
        perms.includes("hr.*");
      const hasDeactivate =
        perms.includes("employee:deactivate") ||
        perms.includes("*") ||
        perms.includes("hr.*");
      const hasExportSel =
        perms.includes("employee:export:selected") ||
        perms.includes("*") ||
        perms.includes("hr.*");
      setCanCreateEmployee(hasCreate);
      setCanEditEmployee(hasUpdate);
      setCanDeleteEmployee(hasDelete);
      setCanExportExcel(hasExportExcel);
      setCanExportPDF(hasExportPDF);
      setCanDownloadTemplate(hasDownloadTemplate);
      setCanBulkUpload(hasBulkUpload);
      setCanUploadDocuments(hasUploadDoc);
      setCanRetireEmployee(hasRetire);
      setCanGenerateCredentials(hasGenCred);
      setCanActivateEmployee(hasActivate);
      setCanDeactivateEmployee(hasDeactivate);
      setCanExportSelected(hasExportSel);
    } else {
      setCanCreateEmployee(false);
      setCanEditEmployee(false);
      setCanDeleteEmployee(false);
      setCanExportExcel(false);
      setCanExportPDF(false);
      setCanDownloadTemplate(false);
      setCanBulkUpload(false);
      setCanUploadDocuments(false);
      setCanRetireEmployee(false);
      setCanGenerateCredentials(false);
      setCanActivateEmployee(false);
      setCanDeactivateEmployee(false);
      setCanExportSelected(false);
    }
  }, [user]);

  const rowCanEdit = (emp) => {
    if (!user || !Array.isArray(user.permissions)) return false;
    const perms = user.permissions;
    if (perms.includes("*") || perms.includes("hr.*") || perms.includes("employee:update") || perms.includes("employee.update")) {
      return true;
    }
    return false;
  };

  const rowCanDelete = (emp) => {
    if (!user || emp.id === user.employee_id) return false;
    if (!Array.isArray(user.permissions)) return false;
    const perms = user.permissions;
    if (perms.includes("*") || perms.includes("hr.*") || perms.includes("employee:delete") || perms.includes("employee.delete")) {
      return true;
    }
    return false;
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      // Calculate offset from current page (backend uses offset, not page)
      const offset = (currentPage - 1) * itemsPerPage;

      const useAdvanced =
        showAdvancedFilters || Object.values(advFilters).some((v) => v);
      let response;

      if (useAdvanced) {
        const params = new URLSearchParams({
          skip: offset,
          limit: itemsPerPage,
        });
        if (debouncedSearchTerm.trim())
          params.append("q", debouncedSearchTerm.trim());
        if (filterStatus !== "all") {
          if (filterStatus === "retired" && retirementSubFilter) {
            // Retired + specific retirement category selected
            params.append("employment_status", retirementSubFilter);
          } else if (filterStatus === "retired" && !retirementSubFilter) {
            // Retired + All Retirement Types selected - send "retired" to get all retirement categories
            params.append("employment_status", "retired");
          } else {
            // Active or Inactive
            params.append("employment_status", filterStatus);
          }
        }
        Object.entries(advFilters).forEach(([k, v]) => {
          // Skip employment_status here — it's already handled by filterStatus/retirementSubFilter above
          if (v && k !== "employment_status") params.append(k, v);
        });
        response = await fetch(buildApiUrl(`/api/v1/employee-master/search/advanced?${params}`),
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          },
        );
      } else {
        const queryParams = new URLSearchParams({
          offset: offset,
          limit: itemsPerPage,
        });

        if (debouncedSearchTerm.trim()) {
          queryParams.append("query", debouncedSearchTerm.trim());
        }

        if (filterStatus !== "all") {
          if (filterStatus === "retired" && retirementSubFilter) {
            // Retired + specific retirement category selected
            queryParams.append("status", retirementSubFilter);
          } else if (filterStatus === "retired" && !retirementSubFilter) {
            // Retired + All Retirement Types selected - send "retired" to get all retirement categories
            queryParams.append("status", "retired");
          } else {
            // Active or Inactive
            queryParams.append("status", filterStatus);
          }
        }

        response = await fetch(buildApiUrl(`/api/v1/benfish-employees/search?${queryParams}`),
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          },
        );
      }

      if (response.ok) {
        const data = await response.json();
        const list = data.items || data.employees || [];
        const mappedEmployees = list.map((emp) => ({
          ...emp,
          emp_name: emp.full_name || emp.emp_name,
        }));
        setEmployees(mappedEmployees);
        setFilteredEmployees(mappedEmployees);
        const total = data.total || data.total_count || 0;
        const calculatedPages = total > 0 ? Math.ceil(total / itemsPerPage) : 1;
        setTotalPages(calculatedPages);
        setTotalCount(total);
        if (currentPage > calculatedPages && calculatedPages > 0) {
          setCurrentPage(calculatedPages);
        }
      } else if (response.status === 403) {
        setError("You do not have permission to view the employee list.");
      } else {
        setError("Failed to fetch employees");
      }
    } catch (error) {
      setError("Network error while fetching employees");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      // Remove current page employees from selected
      const currentPageIds = filteredEmployees.map((emp) => emp.id);
      setSelectedEmployees((prev) =>
        prev.filter((id) => !currentPageIds.includes(id))
      );
    } else {
      // Add current page employees to selected
      const currentPageIds = filteredEmployees.map((emp) => emp.id);
      setSelectedEmployees((prev) => {
        const newSelected = [...prev];
        currentPageIds.forEach((id) => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });
        return newSelected;
      });
    }
  };

  const handleSelectEmployee = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId],
    );
  };

  const handleBulkUpload = () => {
    setShowBulkUploadModal(true);
    setBulkUploadFile(null);
    setBulkUploadError("");
    setBulkUploadSuccess("");
    setUploadPreview(null);
  };

  const handleBulkEmployeeUpload = async () => {
    if (!bulkUploadFile) {
      setBulkUploadError("Please select a file to upload");
      return;
    }

    // Validate file extension
    if (!bulkUploadFile.name.toLowerCase().endsWith(".xlsx")) {
      setBulkUploadError("Only .xlsx files are accepted");
      return;
    }

    try {
      setBulkUploading(true);
      setBulkUploadError("");
      setBulkUploadSuccess("");

      const formData = new FormData();
      formData.append("file", bulkUploadFile);

      const response = await fetch(buildApiUrl("/api/v1/bulk-employees/upload"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        console.log("Bulk upload response:", result);
        setBulkUploadSuccess("Excel parsed and validated successfully.");
        setUploadPreview(result);
      } else {
        throw new Error(result.detail || "Upload failed");
      }
    } catch (error) {
      setBulkUploadError(error.message || "Upload failed. Please try again.");
      // Keep the selected file so user can retry
    } finally {
      setBulkUploading(false);
    }
  };

  const handleImportBulkEmployees = async () => {
    if (!uploadPreview || !uploadPreview.preview) return;

    try {
      setBulkUploading(true);
      setBulkUploadError("");
      setBulkUploadSuccess("");

      const response = await fetch(buildApiUrl("/api/v1/bulk-employees/import"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify(uploadPreview.preview),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(`Successfully imported ${result.successful_records} employees! Failed: ${result.failed_records}.`);
        setShowBulkUploadModal(false);
        setUploadPreview(null);
        setBulkUploadFile(null);
        fetchEmployees();
      } else {
        throw new Error(result.detail || "Import failed");
      }
    } catch (error) {
      setBulkUploadError(error.message || "Import failed. Please try again.");
    } finally {
      setBulkUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(buildApiUrl("/api/v1/employee-master/download-template"),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      );

      if (!response.ok) {
        setError("Unable to download template");
        return;
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");

      a.href = url;

      a.download = "Employee_Bulk_Template.xlsx";

      a.click();

      window.URL.revokeObjectURL(url);
    } catch {
      setError("Failed to download template");
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedEmployees.length === 0) {
      setError("Please select employees to perform bulk action");
      return;
    }

    setBulkAction(action);
    setShowBulkModal(true);
  };

  const executeBulkAction = async () => {
    try {
      if (bulkAction === "export") {
        const selected = filteredEmployees.filter((e) =>
          selectedEmployees.includes(e.id),
        );
        const headers = [
          "Code",
          "Name",
          "Email",
          "Department",
          "Designation",
          "Status",
        ];
        const rows = selected.map((e) => [
          e.emp_code,
          e.full_name || `${e.first_name} ${e.last_name}`,
          e.email || "",
          e.department_name || e.department || "",
          e.emp_desig || e.designation || "",
          e.status || "",
        ]);
        const csv = [headers, ...rows]
          .map((r) =>
            r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","),
          )
          .join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `employees_selected_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        setSuccess(`Exported ${selected.length} employees`);
        setSelectedEmployees([]);
        setShowBulkModal(false);
        setBulkAction("");
        return;
      }

      const response = await fetch(buildApiUrl(`/api/v1/employee-master/bulk-${bulkAction}`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({
            employee_ids: selectedEmployees,
          }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setSuccess(
          `Bulk ${bulkAction} completed (${data.updated ?? selectedEmployees.length} employees)`,
        );
        setSelectedEmployees([]);
        fetchEmployees();
      } else {
        setError(data.detail || `Failed to execute bulk ${bulkAction}`);
      }
    } catch (error) {
      setError("Network error during bulk operation");
    } finally {
      setShowBulkModal(false);
      setBulkAction("");
    }
  };

  const handleEmployeeAction = async (employeeId, action) => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/employee-master/${employeeId}/${action}`),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setSuccess(`Employee ${action} successful`);
        fetchEmployees();
      } else {
        setError(data.detail || `Failed to ${action} employee`);
      }
    } catch (error) {
      setError(`Network error during ${action}`);
    }
  };

  const exportEmployees = async (format = "excel") => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/employee-master/reports/directory/export?format=${format}`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const extension = format === "pdf" ? "pdf" : "xlsx";
        a.download = `employees_${new Date().toISOString().split("T")[0]}.${extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setError("Failed to export employees");
      }
    } catch (error) {
      setError("Network error during export");
    }
  };

  const getStatusColor = (status) => {
    const s = String(status || "").toUpperCase();
    const colors = {
      ACTIVE: "bg-green-100 text-green-800",
      INACTIVE: "bg-red-100 text-red-800",
      SUSPENDED: "bg-yellow-100 text-yellow-800",
      TERMINATED: "bg-gray-100 text-gray-800",
      RETIRED: "bg-slate-100 text-slate-800",
      SUPERANNUATION: "bg-indigo-100 text-indigo-800",
      DEAD: "bg-rose-100 text-rose-800",
      VOLUNTARY: "bg-orange-100 text-orange-800",
      DECEASED: "bg-rose-100 text-rose-800",
    };
    return colors[s] || "bg-gray-100 text-gray-800";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const handleEmployeeFormSuccess = (employee) => {
    setSuccess(
      editMode
        ? "Employee updated successfully!"
        : "Employee created successfully!",
    );
    setShowEmployeeModal(false);
    setSelectedEmployee(null);
    setEditMode(false);
    fetchEmployees(); // Refresh the list
  };

  const handleViewEmployee = async (employee) => {
    try {
      setError("");
      setLoading(true);

      // Fetch fresh employee data from server
      const response = await fetch(buildApiUrl(`/api/v1/employee-master/${employee.id}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      if (response.ok) {
        const masterData = await response.json();
        setSelectedEmployee(mapEmployeeMasterToForm(masterData));
        setEditMode(false);
        setShowEmployeeModal(true);
      } else {
        // Fallback to cached data if API fails
        setSelectedEmployee(employee);
        setEditMode(false);
        setShowEmployeeModal(true);
        setError(
          "Using cached data - unable to fetch latest employee information",
        );
      }
    } catch (error) {
      // Fallback to cached data on network error
      setSelectedEmployee(employee);
      setEditMode(false);
      setShowEmployeeModal(true);
      setError("Using cached data - network error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleEditEmployee = async (employee) => {
    try {
      setError("");
      setLoading(true);

      // Fetch complete employee data for editing
      const response = await fetch(buildApiUrl(`/api/v1/employee-master/${employee.id}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      if (response.ok) {
        const masterData = await response.json();
        setSelectedEmployee(mapEmployeeMasterToForm(masterData));
        setEditMode(true);
        setShowEmployeeModal(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(
          errorData.detail || "Unable to fetch complete employee details for editing. Please try again."
        );
      }
    } catch (error) {
      setError("Network error: Unable to load employee details.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmployee = (employee) => {
    setEmployeeToDelete(employee);
    setShowDeleteModal(true);
  };

  const handleRetireEmployee = (employee) => {
    const currentStatus = String(
      employee?.status || employee?.employment_status || "",
    ).toLowerCase();
    const empStatus = String(employee?.employment_status || "").toLowerCase();
    const roleName = String(
      employee?.role_name || employee?.hospital_role_name || "",
    ).toLowerCase();
    const retiredStatuses = ["retired", "superannuation", "dead", "voluntary", "deceased"];
    if (retiredStatuses.includes(currentStatus) || retiredStatuses.includes(empStatus) || roleName === "pensioner") {
      return;
    }
    setEmployeeToRetire(employee);
    setSeparationType('retirement');
    setShowRetireModal(true);
  };

  const handleSeparationAction = (employee, type) => {
    const currentStatus = String(
      employee?.status || employee?.employment_status || "",
    ).toLowerCase();
    const empStatus = String(employee?.employment_status || "").toLowerCase();
    const roleName = String(
      employee?.role_name || employee?.hospital_role_name || "",
    ).toLowerCase();
    const retiredStatuses = ["retired", "superannuation", "dead", "voluntary", "deceased"];
    if (retiredStatuses.includes(currentStatus) || retiredStatuses.includes(empStatus) || roleName === "pensioner") {
      return;
    }
    setEmployeeToRetire(employee);
    setSeparationType(type);
    setShowRetireModal(true);
    setShowSeparationDropdown(null);
  };

  const handleManageDocuments = (employee) => {
    setDocumentEmployee(employee);
    setShowDocumentsModal(true);
  };

  const handleDocumentsModalClose = () => {
    setShowDocumentsModal(false);
    setDocumentEmployee(null);
    fetchEmployees(); // Refresh list to update document status
  };

  const handleConfirmDelete = async () => {
    if (!employeeToDelete) return;

    try {
      setDeleting(true);
      setError("");
      setSuccess("");

      const response = await fetch(buildApiUrl(`/api/v1/benfish-employees/${employeeToDelete.id}`),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      );

      const data = await response.json();

      if (response.ok) {
        setSuccess(
          data.message ||
          `Employee ${employeeToDelete.emp_code} deleted successfully`,
        );
        await fetchEmployees(); // Refresh the list
        setShowDeleteModal(false);
        setEmployeeToDelete(null);

        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(""), 5000);
      } else {
        // Handle specific business rule violations
        if (response.status === 400) {
          setError(
            data.detail || "Cannot delete employee due to business constraints",
          );
        } else if (response.status === 403) {
          setError("You do not have permission to delete employees");
        } else {
          setError(data.detail || "Failed to delete employee");
        }

        // Clear error message after 10 seconds
        setTimeout(() => setError(""), 10000);
      }
    } catch (error) {
      setError("Network error occurred while deleting employee");
      setTimeout(() => setError(""), 10000);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setEmployeeToDelete(null);
  };

  const handleAddEmployee = () => {
    if (!canCreateEmployee) {
      setError("You do not have permission to add hospital employees.");
      return;
    }
    setSelectedEmployee(null);
    setEditMode(false);
    setShowEmployeeModal(true);
  };

  const handleGenerateCredentials = (employee) => {
    setCredentialEmployee(employee);
    setCredentialResult(null);
    setShowCredentialModal(true);
  };

  const generateUserCredentials = async (employeeId) => {
    setGeneratingCredentials(true);
    try {
      const response = await fetch(buildApiUrl(`/api/v1/auth/generate-credentials/${employeeId}`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      );

      const result = await response.json();
      setCredentialResult(result);

      if (result.success) {
        setSuccess("User credentials generated successfully!");
        fetchEmployees(); // Refresh the list to update credential status
      }
    } catch (error) {
      setError("Failed to generate credentials. Please try again.");
    } finally {
      setGeneratingCredentials(false);
    }
  };

  return (
    <div className="max-w-full mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <Users className="w-6 h-6 mr-2 text-blue-600" />
            Employee Management
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Enterprise employee master — single source of truth for payroll,
            leave, attendance, and hierarchy.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canExportExcel && (
            <button
              onClick={() => exportEmployees("excel")}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          )}

          {canExportPDF && (
            <button
              onClick={() => exportEmployees("pdf")}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export PDF
            </button>
          )}

          {canDownloadTemplate && (
            <button
              onClick={handleDownloadTemplate}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download Template
            </button>
          )}

          {canBulkUpload && (
            <button
              onClick={handleBulkUpload}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Bulk Upload
            </button>
          )}

          {canCreateEmployee && (
            <button
              onClick={handleAddEmployee}
              disabled={!canCreateEmployee}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${canCreateEmployee
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-600 cursor-not-allowed"
                }`}
            >
              <Plus className="w-4 h-4" />
              Add Employee
            </button>
          )}
        </div>
      </div>

      <EmployeeMasterKPIs />

      <div className="flex gap-2 mb-4">
        {["list", "pensioners", "departmentConfig", "gisConfig", "roleManagement"].filter(v => {
          if (v === 'roleManagement') {
            return hasPermission && (
              hasPermission('role:view:system') ||
              hasPermission('role:view:custom') ||
              hasPermission('role:update') ||
              hasPermission('role:delete') ||
              hasPermission('role:assign') ||
              hasPermission('role:view:self')
            );
          }
          if (v === 'departmentConfig') return hasPermission && hasPermission('department:view');
          if (v === 'pensioners') {
            return hasPermission && (
              hasPermission('pensioner:view:own') ||
              hasPermission('pensioner:view:dept') ||
              hasPermission('pensioner:view:all')
            );
          }
          if (v === 'gisConfig') {
            return hasPermission && (
              hasPermission('gis:view:dept') ||
              hasPermission('gis:view:all') ||
              hasPermission('gis:save')
            );
          }
          return true;
        }).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setSectionView(v)}
            className={`px-4 py-2 rounded-lg text-sm capitalize ${sectionView === v ? "bg-blue-600 text-white" : "bg-white border text-gray-700"}`}
          >
            {v === "list"
              ? "Employee List"
              : v === "pensioners"
                ? "Pensioner Profiles"
                : v === "departmentConfig"
                  ? "Department Configuration"
                  : v === "gisConfig"
                    ? "GIS Configuration"
                    : "Role Management"}
          </button>
        ))}
      </div>
      {/* 
      {sectionView === 'hierarchy' && <HierarchyTreeView />} */}
      {sectionView === "pensioners" && (
        <PensionerProfilesPanel refreshToken={pensionerRefreshKey} />
      )}
      {sectionView === "permissions" && <RolePermissionMatrix />}
      {sectionView === "departmentConfig" && <DepartmentConfiguration />}
      {sectionView === "gisConfig" && <GISConfiguration />}
      {sectionView === "roleManagement" && <RoleManagement />}
      {sectionView === "list" && (
        <>
          {/* <EmployeeMasterWorkflow /> */}

          {/* Search and Filters */}
          <div className="mb-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className={`relative ${filterStatus === "retired" ? "md:col-span-3" : "md:col-span-6"}`}>
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by name, code, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-3">
                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setRetirementSubFilter("");
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="retired">Retired</option>
                </select>
              </div>

              {filterStatus === "retired" && (
                <div className="md:col-span-3">
                  <select
                    value={retirementSubFilter}
                    onChange={(e) => setRetirementSubFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Retirement Types</option>
                    <option value="superannuation">Superannuation</option>
                    <option value="voluntary_retirement">Voluntary Retirement</option>
                    <option value="medical_retirement">Medical Retirement</option>
                    <option value="compulsory_retirement">Compulsory Retirement</option>
                    <option value="death_in_service">Death in Service</option>
                    <option value="absconding">Absconding</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              )}

              <div className="md:col-span-3">
                <button
                  type="button"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                >
                  {showAdvancedFilters ? "Hide" : "Advanced"} Filters
                </button>
              </div>
            </div>

            {showAdvancedFilters && masters && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg border">
                <select
                  value={advFilters.department_id}
                  onChange={(e) =>
                    setAdvFilters((f) => ({
                      ...f,
                      department_id: e.target.value,
                    }))
                  }
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value="">All Departments</option>
                  {masters.departments?.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
                <select
                  value={advFilters.role_id}
                  onChange={(e) =>
                    setAdvFilters((f) => ({ ...f, role_id: e.target.value }))
                  }
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value="">All Roles</option>
                  {masters.roles?.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <select
                  value={advFilters.grade_master_id}
                  onChange={(e) =>
                    setAdvFilters((f) => ({
                      ...f,
                      grade_master_id: e.target.value,
                    }))
                  }
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value="">All Grades</option>
                  {masters.grades?.map((g) => (
                    <option key={g.value} value={g.value}>
                      {g.label}
                    </option>
                  ))}
                </select>
                <SearchableSelect
                  value={advFilters.designation_id}
                  onChange={(value) =>
                    setAdvFilters((f) => ({
                      ...f,
                      designation_id: value,
                    }))
                  }
                  options={[
                    { value: "", label: "All Designations" },
                    ...(masters.designations?.map((d) => ({
                      value: d.value,
                      label: d.label,
                    })) || []),
                  ]}
                  placeholder="Search designations..."
                />
                <SearchableSelect
                  value={advFilters.head_code}
                  onChange={(value) =>
                    setAdvFilters((f) => ({ ...f, head_code: value }))
                  }
                  options={[
                    { value: "", label: "All Heads" },
                    ...availableHeads,
                  ]}
                  placeholder="Search heads..."
                />
              </div>
            )}

            {/* Bulk Actions */}
            {selectedEmployees.length > 0 && (
              <div className="flex items-center justify-between bg-blue-50 p-4 rounded-lg border">
                <span className="text-blue-700 font-medium">
                  {selectedEmployees.length} employee
                  {selectedEmployees.length > 1 ? "s" : ""} selected
                </span>
                <div className="flex items-center gap-2">
                  {canActivateEmployee && (
                    <button
                      onClick={() => handleBulkAction("activate")}
                      className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm flex items-center gap-1"
                    >
                      <UserCheck className="w-3 h-3" />
                      Activate
                    </button>
                  )}
                  {canDeactivateEmployee && (
                    <button
                      onClick={() => handleBulkAction("deactivate")}
                      className="px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm flex items-center gap-1"
                    >
                      <UserX className="w-3 h-3" />
                      Deactivate
                    </button>
                  )}
                  {canExportSelected && (
                    <button
                      onClick={() => handleBulkAction("export")}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" />
                      Export Selected
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Employee Table */}
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <ScrollableTableContainer
              header={null}
              maxHeight={600}
              showTopScrollbar={true}
              stickyHeader={true}
            >
              <table className="w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                      Photo
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                      Employee
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                      Contact
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                      Department
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                      Scale
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                      Designation
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                      Documents
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                      Date of Retirement
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredEmployees.length === 0 ? (
                    <tr>
                      <td
                        colSpan="9"
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No employees found</p>
                      </td>
                    </tr>
                  ) : (
                    filteredEmployees.map((employee) => (
                      <tr key={employee.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedEmployees.includes(employee.id)}
                            onChange={() => handleSelectEmployee(employee.id)}
                            className="rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <EmployeeAvatar
                            employeeId={employee.id}
                            size="sm"
                            borderClass="border-2 border-gray-200"
                            fallbackClass="bg-gray-200 text-gray-400"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900">
                              {employee.emp_name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {employee.emp_code}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <div className="flex items-center mb-1">
                              <Mail className="w-3 h-3 mr-1 text-gray-400" />
                              <span className="text-gray-600">
                                {employee.email || "N/A"}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <Phone className="w-3 h-3 mr-1 text-gray-400" />
                              <span className="text-gray-600">
                                {employee.phone || "N/A"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {employee.department || "N/A"}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {employee.scale || "N/A"}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {employee.emp_desig || "N/A"}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {employee.documents_complete ? (
                            <div className="flex items-center">
                              <Check className="w-4 h-4 text-green-600 mr-1" />
                              <span className="text-sm font-medium text-green-700">Complete</span>
                            </div>
                          ) : (
                            <div className="text-sm">
                              <p className="font-medium text-gray-900">
                                {employee.documents_uploaded || 0} / {employee.documents_required || 4}
                              </p>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-900">
                            {formatDate(employee.retirement_date)}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                ["retired", "superannuation", "dead", "voluntary", "deceased"].includes(String(employee.employment_status || "").toLowerCase())
                                  ? employee.employment_status
                                  : employee.status
                              )}`}
                            >
                              {(["retired", "superannuation", "dead", "voluntary", "deceased"].includes(String(employee.employment_status || "").toLowerCase())
                                ? employee.employment_status
                                : (employee.status || "ACTIVE")).toUpperCase()}
                            </span>
                            <div>
                              {employee.has_user_account ? (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Has Login
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                  No Login
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() =>
                                navigate(`/employees/${employee.id}/profile`)
                              }
                              className="p-1 text-blue-600 hover:text-blue-800"
                              title="360° Profile"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleManageDocuments(employee)}
                              disabled={!canUploadDocuments}
                              className={`p-1 ${canUploadDocuments ? "text-amber-600 hover:text-amber-800" : "text-gray-400 cursor-not-allowed"}`}
                              title={canUploadDocuments ? "Manage Documents" : "Manage documents permission required"}
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditEmployee(employee)}
                              disabled={!rowCanEdit(employee)}
                              className={`p-1 ${rowCanEdit(employee) ? "text-green-600 hover:text-green-800" : "text-gray-400 cursor-not-allowed"}`}
                              title={
                                rowCanEdit(employee)
                                  ? "Edit Employee"
                                  : "Edit permission required"
                              }
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {String(employee?.status || "").toLowerCase() === "active" ? (
                              <button
                                onClick={() => handleRetireEmployee(employee)}
                                disabled={!rowCanEdit(employee) || !canRetireEmployee}
                                className={`p-1 ${rowCanEdit(employee) && canRetireEmployee ? "text-indigo-600 hover:text-indigo-800" : "text-gray-400 cursor-not-allowed"}`}
                                title={
                                  !canRetireEmployee
                                    ? "Separation permission required"
                                    : (rowCanEdit(employee)
                                      ? "Process Separation (Retirement / Deceased / Voluntary)"
                                      : "Edit permission required for this record")
                                }
                              >
                                <UserX className="w-4 h-4" />
                              </button>
                            ) : (
                              (["retired", "superannuation", "dead", "voluntary", "deceased"].includes(
                                String(employee?.employment_status || "").toLowerCase()
                              ) ||
                                String(
                                  employee?.role_name ||
                                  employee?.hospital_role_name ||
                                  "",
                                ).toLowerCase() === "pensioner") && (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() =>
                                      navigate(
                                        `/pensioners/employee/${employee.id}`,
                                      )
                                    }
                                    className="p-1 text-slate-600 hover:text-slate-800"
                                    title="View Pension Profile"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                                    Separated
                                  </span>
                                </div>
                              )
                            )}
                            {!employee.has_user_account && (
                              <button
                                onClick={() =>
                                  handleGenerateCredentials(employee)
                                }
                                disabled={!canGenerateCredentials || employee.documents_complete === false}
                                className={`p-1 ${(!canGenerateCredentials || employee.documents_complete === false) ? "text-gray-400 cursor-not-allowed" : "text-purple-600 hover:text-purple-800"}`}
                                title={!canGenerateCredentials ? "Generate credentials permission required" : (employee.documents_complete === false ? "Upload all mandatory documents before generating employee credentials." : "Generate User Credentials")}
                              >
                                <UserPlus className="w-4 h-4" />
                              </button>
                            )}
                            {/* Delete button hidden as per requirement */}
                            {/* {rowCanDelete(employee) ? (
                              <button
                                onClick={() => handleDeleteEmployee(employee)}
                                className="p-1 text-red-600 hover:text-red-800"
                                title="Delete Employee"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            ) : (
                              employee.id !== user.employee_id && (
                                <button
                                  disabled
                                  className="p-1 text-gray-400 cursor-not-allowed"
                                  title="Delete permission required"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )
                            )} */}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </ScrollableTableContainer>
          )}

          {/* Pagination - Bottom-Right Corner of Employee Table */}
          {!loading && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-end gap-4 border-t border-gray-200 pt-4 px-2">
              <div className="text-sm text-gray-600 font-medium">
                {filteredEmployees.length > 0 && totalCount > 0 ? (
                  <>
                    Showing{" "}
                    <span className="font-semibold text-gray-900">
                      {(currentPage - 1) * itemsPerPage + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-semibold text-gray-900">
                      {Math.min(
                        (currentPage - 1) * itemsPerPage +
                        filteredEmployees.length,
                        totalCount,
                      )}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-gray-900">
                      {totalCount}
                    </span>{" "}
                    entries
                  </>
                ) : (
                  "No entries found"
                )}
              </div>

              <div className="flex items-center space-x-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => {
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`min-w-[36px] px-3 py-1.5 text-sm font-semibold rounded-md border transition-colors shadow-sm ${currentPage === page
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (
                      (page === currentPage - 2 && page > 1) ||
                      (page === currentPage + 2 && page < totalPages)
                    ) {
                      return (
                        <span
                          key={page}
                          className="px-2 py-1 text-gray-400 font-medium select-none"
                        >
                          ...
                        </span>
                      );
                    }
                    return null;
                  },
                )}

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages || totalPages <= 1}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Bulk Action Confirmation Modal */}
          <AnimatePresence>
            {showBulkModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Confirm Bulk Action
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Are you sure you want to {bulkAction}{" "}
                    {selectedEmployees.length} selected employee
                    {selectedEmployees.length > 1 ? "s" : ""}?
                  </p>
                  <div className="flex items-center justify-end space-x-3">
                    <button
                      onClick={() => setShowBulkModal(false)}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={executeBulkAction}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Confirm
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showBulkUploadModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
              >
                <motion.div
                  initial={{ scale: 0.95, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.95, y: 20 }}
                  className={`bg-white rounded-2xl shadow-2xl p-6 w-full ${uploadPreview ? "max-w-6xl" : "max-w-lg"
                    } transition-all duration-300 max-h-[90vh] flex flex-col`}
                >
                  <div className="flex items-center justify-between border-b pb-4 mb-4">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <Upload className="w-5 h-5 text-blue-600" />
                      Bulk Employee Upload Validation
                    </h2>
                    <button
                      onClick={() => {
                        setShowBulkUploadModal(false);
                        setUploadPreview(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  {!uploadPreview ? (
                    <div className="flex-1 flex flex-col justify-between">
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center bg-gray-50 hover:bg-gray-100 transition-colors">
                        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                        <p className="font-semibold text-gray-700">Select Employee Excel File</p>
                        <p className="text-xs text-gray-500 mt-1">Accepts .xlsx files only. Must contain required headers.</p>

                        <input
                          type="file"
                          accept=".xlsx"
                          className="mt-6 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                          onChange={(e) => {
                            setBulkUploadFile(e.target.files?.[0] || null);
                            setBulkUploadError("");
                            setBulkUploadSuccess("");
                          }}
                        />
                        {bulkUploadFile && (
                          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-emerald-600 bg-emerald-50 py-2 px-3 rounded-lg border border-emerald-100">
                            <Check className="w-4 h-4" />
                            Selected: {bulkUploadFile.name}
                          </div>
                        )}
                        {bulkUploadError && (
                          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700 text-sm text-left">
                            <div className="font-semibold flex items-center gap-1.5 mb-0.5">
                              <AlertCircle className="w-4 h-4 text-red-500" />
                              Validation Failed
                            </div>
                            {bulkUploadError}
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end gap-3 mt-6 border-t pt-4">
                        <button
                          onClick={() => setShowBulkUploadModal(false)}
                          className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleBulkEmployeeUpload}
                          disabled={bulkUploading || !bulkUploadFile}
                          className={`px-6 py-2.5 rounded-xl text-white font-medium text-sm flex items-center gap-2 transition-colors ${bulkUploading || !bulkUploadFile
                            ? "bg-blue-300 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                            }`}
                        >
                          {bulkUploading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Validating...
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4" />
                              Validate File
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 overflow-hidden flex flex-col">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 p-4 rounded-xl shadow-sm text-center">
                          <p className="text-sm font-medium text-blue-800 uppercase tracking-wider">Total Rows</p>
                          <p className="text-3xl font-extrabold text-blue-900 mt-1">{uploadPreview.totalRows}</p>
                        </div>
                        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 p-4 rounded-xl shadow-sm text-center">
                          <p className="text-sm font-medium text-emerald-800 uppercase tracking-wider">Valid Rows</p>
                          <p className="text-3xl font-extrabold text-emerald-900 mt-1">{uploadPreview.validRows}</p>
                        </div>
                        <div className="bg-gradient-to-br from-rose-50 to-rose-100 border border-rose-200 p-4 rounded-xl shadow-sm text-center">
                          <p className="text-sm font-medium text-rose-800 uppercase tracking-wider">Invalid Rows</p>
                          <p className="text-3xl font-extrabold text-rose-900 mt-1">{uploadPreview.invalidRows}</p>
                        </div>
                      </div>

                      {/* Info Alert */}
                      <div className="bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded-lg text-xs font-medium mb-4 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                          <strong>Validation Preview Mode:</strong> This view performs schema, formatting, and database master checks. No database records will be saved during this validation check.
                        </div>
                      </div>

                      {/* Preview Table */}
                      <div className="flex-1 overflow-y-auto border border-gray-200 rounded-xl">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Row</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Employee Name</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Department</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Designation</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Status</th>
                              <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Validation Errors</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {uploadPreview.preview.map((rowItem, idx) => {
                              const data = rowItem.data || {};
                              const name = [data["First Name"], data["Last Name"]].filter(Boolean).join(" ") || "N/A";
                              return (
                                <tr key={idx} className={rowItem.status === "INVALID" ? "bg-rose-50/20" : "hover:bg-gray-50"}>
                                  <td className="px-4 py-3 text-sm text-gray-500 font-semibold">{rowItem.rowNumber}</td>
                                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{name}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{data["Department"] || "N/A"}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{data["Designation"] || "N/A"}</td>
                                  <td className="px-4 py-3 text-sm">
                                    <span
                                      className={`px-2 py-1 rounded-full text-xs font-bold ${rowItem.status === "VALID"
                                        ? "bg-emerald-100 text-emerald-800"
                                        : "bg-rose-100 text-rose-800"
                                        }`}
                                    >
                                      {rowItem.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-rose-600 font-medium">
                                    {rowItem.errors.length > 0 ? (
                                      <ul className="list-disc pl-4 space-y-0.5">
                                        {rowItem.errors.map((err, errIdx) => (
                                          <li key={errIdx}>{err}</li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <span className="text-emerald-600">✓ No errors</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Footer Actions */}
                      <div className="flex justify-between items-center mt-6 border-t pt-4">
                        <button
                          onClick={() => setUploadPreview(null)}
                          className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium"
                        >
                          Upload Another File
                        </button>

                        <div className="flex gap-3">
                          <button
                            onClick={() => {
                              setShowBulkUploadModal(false);
                              setUploadPreview(null);
                            }}
                            className="px-5 py-2.5 bg-gray-800 hover:bg-gray-900 text-white rounded-xl transition-colors text-sm font-medium"
                          >
                            Close Preview
                          </button>

                          <button
                            onClick={handleImportBulkEmployees}
                            disabled={bulkUploading || uploadPreview.validRows === 0}
                            className={`px-5 py-2.5 rounded-xl text-sm font-medium border text-white transition-colors ${bulkUploading || uploadPreview.validRows === 0
                              ? "bg-emerald-300 border-emerald-300 cursor-not-allowed"
                              : "bg-emerald-600 border-emerald-600 hover:bg-emerald-700"
                              }`}
                          >
                            {bulkUploading ? "Importing..." : `Import (${uploadPreview.validRows} Valid Rows)`}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      {/* Employee Creation/Edit Form */}
      <EmployeeCreationForm
        isOpen={showEmployeeModal}
        onClose={() => {
          setShowEmployeeModal(false);
          setSelectedEmployee(null);
          setEditMode(false);
        }}
        onSuccess={handleEmployeeFormSuccess}
        editEmployee={selectedEmployee}
        viewMode={selectedEmployee && !editMode}
        editMode={editMode}
      />

      {/* Credential Generation Modal */}
      <CredentialGenerationModal
        isOpen={showCredentialModal}
        onClose={() => {
          setShowCredentialModal(false);
          setCredentialEmployee(null);
          setCredentialResult(null);
          setGeneratingCredentials(false);
        }}
        employee={credentialEmployee}
        onGenerate={generateUserCredentials}
        result={credentialResult}
        isGenerating={generatingCredentials}
      />

      {/* Employee Deletion Modal */}
      <EmployeeDeletionModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        employee={employeeToDelete}
        loading={deleting}
      />

      {/* Retire Employee Modal */}
      <RetireEmployeeModal
        isOpen={showRetireModal}
        onClose={() => {
          setShowRetireModal(false);
          setSeparationType(null);
        }}
        employee={employeeToRetire}
        separationType={separationType}
        onSuccess={() => {
          fetchEmployees();
          setPensionerRefreshKey((prev) => prev + 1);
        }}
      />

      {/* Manage Documents Modal */}
      <ManageDocumentsModal
        isOpen={showDocumentsModal}
        onClose={handleDocumentsModalClose}
        employee={documentEmployee}
      />
    </div>
  );
};

export default EmployeeManagement;
